import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import { UserService } from '@app/core/services/user.service';
import { FeatureFlagKey } from '@dwo/enums';
import { WorkOrderDetailsDto } from '@app/generated/model';
import { WorkOrderControllerService, WorkOrderObjectsListControllerService } from '@app/generated/service';
import {
  CreateWorkOrderSiteQueryRequest,
  SiteQueryCaseType,
  SiteQueryReason,
  SiteQueryReference,
} from './site-query.model';
import { WorkOrderSiteQueryService } from './site-query.service';
import { formatFileSize } from '@app/utils';
import { WorkOrderDetailsService } from '../work-order-details.service';
import { LoggingService } from '@app/shared/services/logging.service';

type SiteQueryForm = Omit<CreateWorkOrderSiteQueryRequest, 'reason' | 'queryDate' | 'discipline' | 'taskId'> & {
  reason: SiteQueryReason | null;
  queryDate: string;
};

@Component({
  selector: 'app-create-work-order-site-query',
  templateUrl: './create-work-order-site-query.component.html',
  styleUrls: ['./site-query.component.scss'],
  standalone: false,
})
export class CreateWorkOrderSiteQueryComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private workOrderId = '';

  readonly reasonOptions = [SiteQueryReason.Clarify, SiteQueryReason.Quantity];
  readonly caseTypeOptions = [
    SiteQueryCaseType.ToleranceDeviation,
    SiteQueryCaseType.OffshoreChange,
    SiteQueryCaseType.MaterialRepair,
    SiteQueryCaseType.OtherClarification,
  ];

  workOrder: WorkOrderDetailsDto | null = null;
  taskId = '';
  requiresManualTaskId = false;
  pendingFiles: File[] = [];
  referenceOptions: SiteQueryReference[] = [];
  selectedReferenceKeys: string[] = [];
  referencesLoading = false;
  referencesLoadFailed = false;
  saving = false;
  get workOrderReferenceError(): boolean {
    return !/^[0-9]{4}$/.test(this.taskId);
  }

  form: SiteQueryForm = this.emptyForm();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly workOrderDetailsService: WorkOrderDetailsService,
    private readonly siteQueryService: WorkOrderSiteQueryService,
    private readonly objectController: WorkOrderObjectsListControllerService,
    private readonly workOrderController: WorkOrderControllerService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly userService: UserService,
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
    private readonly log: LoggingService,
  ) {}

  ngOnInit(): void {
    this.workOrderId = this.getWorkOrderId();
    this.workOrderDetailsService
      .getData()
      .pipe(
        filter((workOrder): workOrder is WorkOrderDetailsDto => !!workOrder && workOrder.id === this.workOrderId),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((workOrder) => {
        this.workOrder = workOrder;
        this.changeDetectorRef.markForCheck();
        if (!this.featureFlagService.isEnabled(FeatureFlagKey.EnableSiteQuery, workOrder.projectId)) {
          void this.router.navigate(['/work-order', this.workOrderId], { replaceUrl: true });
          return;
        }
        this.initializeFromWorkOrder(workOrder);
        this.loadReferenceOptions();
      });

    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (!this.form.initiatedBy.trim()) {
          this.form.initiatedBy = (user?.email ?? user?.username ?? '').toString().trim();
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.pendingFiles = [...this.pendingFiles, ...Array.from(input.files ?? [])];
    input.value = '';
  }

  removeFile(fileToRemove: File): void {
    this.pendingFiles = this.pendingFiles.filter((file) => file !== fileToRemove);
  }

  formatFileSize(bytes: number): string {
    return formatFileSize(bytes);
  }

  referenceKey(reference: SiteQueryReference): string {
    return `${reference.type}:${reference.id}`;
  }

  private loadReferenceOptions(): void {
    this.referencesLoading = true;
    this.referencesLoadFailed = false;
    forkJoin({
      tags: this.objectController.getObjects(this.workOrderId),
      documents: this.workOrderController.getWorkOrderDocuments(this.workOrderId),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ tags, documents }) => {
          this.referenceOptions = [
            ...tags
              .filter((tag) => tag.id && tag.objectNumber)
              .map((tag) => ({
                type: 'TAG' as const,
                id: tag.id!,
                number: tag.objectNumber!,
              })),
            ...documents
              .filter((document) => document.id && document.documentNumber)
              .map((document) => ({
                type: 'DOCUMENT' as const,
                id: document.id!,
                number: document.documentNumber!,
              })),
          ];
          this.referencesLoading = false;
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.referencesLoading = false;
          this.referencesLoadFailed = true;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  async save(): Promise<void> {
    if (
      this.saving ||
      !this.workOrder ||
      !this.workOrder.workCenterId?.trim() ||
      this.workOrderReferenceError ||
      !this.form.reason ||
      this.referencesLoading ||
      this.referencesLoadFailed
    ) {
      return;
    }

    this.saving = true;
    try {
      const request: CreateWorkOrderSiteQueryRequest = {
        ...this.form,
        ...(this.requiresManualTaskId ? { taskId: this.taskId.trim() } : {}),
        reason: this.form.reason,
        queryDate: this.form.queryDate || undefined,
        selectedReferences: this.referenceOptions.filter((reference) =>
          this.selectedReferenceKeys.includes(this.referenceKey(reference)),
        ),
      };
      const created = await firstValueFrom(this.siteQueryService.create(this.workOrderId, request));
      this.log.event('SiteQueryCreated', {
        Reason: request.reason,
        AttachmentCount: this.pendingFiles.length,
        ReferenceCount: request.selectedReferences?.length ?? 0,
      });

      if (this.pendingFiles.length > 0) {
        try {
          await Promise.all(
            this.pendingFiles.map((file) =>
              firstValueFrom(this.siteQueryService.uploadAttachment(this.workOrderId, created.id, file)),
            ),
          );
        } catch {
          this.log.event('SiteQueryAttachmentUploadFailed', { AttachmentCount: this.pendingFiles.length });
          this.snackBar.open(this.translate.instant('siteQuery.attachmentUploadError'), 'OK', { duration: 6000 });
          await this.navigateToList();
          return;
        }
      }

      this.snackBar.open(this.translate.instant('siteQuery.createSuccess'), 'OK', { duration: 4000 });
      await this.navigateToList();
    } catch {
      this.log.event('SiteQueryCreateFailed');
      this.snackBar.open(this.translate.instant('siteQuery.createError'), 'OK', { duration: 6000 });
    } finally {
      this.saving = false;
    }
  }

  async cancel(): Promise<void> {
    await this.navigateToList();
  }

  reasonTranslationKey(reason: SiteQueryReason): string {
    return `siteQuery.reason.${reason}`;
  }

  caseTypeTranslationKey(caseType: SiteQueryCaseType): string {
    return `siteQuery.caseType.${caseType}`;
  }

  private initializeFromWorkOrder(workOrder: WorkOrderDetailsDto): void {
    const workOrderNumber = workOrder.workOrderNumber ?? '';
    this.taskId = /^\d{4}/.exec(workOrderNumber)?.[0] ?? '';
    this.requiresManualTaskId = !this.taskId;
    this.form = {
      revision: '01',
      title: '',
      deliveryId: 'NA',
      site: workOrder.siteNo ?? '',
      reason: null,
      caseType: SiteQueryCaseType.ToleranceDeviation,
      quantityChange: false,
      scheduleImpact: false,
      materialNeed: false,
      description: '',
      suggestedSolution: '',
      references: '',
      initiatedBy: this.form.initiatedBy ?? '',
      company: '',
      queryDate: this.toLocalDateTimeInput(new Date()),
      siteLeader: '',
      integrationManager: '',
    };
  }

  private emptyForm(): SiteQueryForm {
    return {
      revision: '01',
      title: '',
      deliveryId: 'NA',
      site: '',
      reason: null,
      caseType: SiteQueryCaseType.ToleranceDeviation,
      quantityChange: false,
      scheduleImpact: false,
      materialNeed: false,
      description: '',
      suggestedSolution: '',
      references: '',
      initiatedBy: '',
      company: '',
      queryDate: '',
      siteLeader: '',
      integrationManager: '',
    };
  }

  private toLocalDateTimeInput(date: Date): string {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
    return localDate.toISOString().slice(0, 16);
  }

  private getWorkOrderId(): string {
    return this.route.parent?.parent?.snapshot.paramMap.get('id') ?? this.route.snapshot.paramMap.get('id') ?? '';
  }

  private navigateToList(): Promise<boolean> {
    return this.router.navigate(['/work-order', this.workOrderId, 'site-queries']);
  }
}
